import React, { useMemo } from 'react';
import type { Editor, Range } from '@tiptap/react';
import {
  SuggestionMenu,
  type SuggestionItem,
} from '@cotera/client/app/components/ui/tiptap/tiptap-ui-utils/suggestion-menu';
import type {
  UpstreamFieldOption,
  UpstreamSourceGroup,
} from '../variable-picker-data';
import { sourceSections, variableFieldMatches } from '../variable-picker';
import {
  VariablePickerList,
  type VariableItemContext,
} from './variable-picker-list';

/**
 * Registers the `/` variable picker on a token editor: typing `/` opens a
 * searchable, keyboard-navigable list of previous-step output fields; selecting
 * one inserts a pill at the caret.
 *
 * Sections and search come from the shared picker, so the same query surfaces
 * the same fields in the same order here as in the bolt popover.
 */
export const VariableSuggestionMenu: React.FC<{
  sources: readonly UpstreamSourceGroup[];
  onInsert: (editor: Editor, field: UpstreamFieldOption, range: Range) => void;
}> = ({ sources, onInsert }) => {
  const items = useMemo<SuggestionItem<VariableItemContext>[]>(
    () =>
      sourceSections(sources).flatMap((section) =>
        section.fields.map((field) => ({
          title: field.leaf,
          group: section.heading,
          context: { field },
          onSelect: ({ editor, range }) => {
            onInsert(editor, field, range);
          },
        }))
      ),
    [sources, onInsert]
  );

  return (
    <SuggestionMenu
      char="/"
      pluginKey="workflowVariablePicker"
      selector="workflow-variable-picker"
      allowedPrefixes={null}
      // The token input lives inside the node inspector's `absolute z-20` panel,
      // which traps this menu's z-index and clips it. Portal to the body so the
      // dropdown paints above the Runs panel and can collision-position against
      // the full viewport instead of going off the page.
      portal
      // onSelect deletes the trigger+query and inserts the pill in one
      // transaction so controlled TokenInputs never see an intermediate ''.
      deleteTriggerOnSelect={false}
      // Filtered here rather than by the generic helper, which also matches a
      // row's value hint — so typing `won` surfaced every field whose sample
      // value happened to be `won`. The popover never did that; now neither
      // does this.
      items={({ query }: { query: string }) =>
        items.filter(
          (item) =>
            item.context !== undefined &&
            variableFieldMatches(item.context.field, query)
        )
      }
    >
      {(props) => <VariablePickerList {...props} />}
    </SuggestionMenu>
  );
};
